import { CategoryEnum, CollectionType, CommentType, ProductType, RecommendationType, SocialLinkType, UserType } from '@/@types';
import { findCategoryBySubCategory, gamification as gamificationMapper } from '@/@constants';
import { mapProviders } from './constants';
import { CDNImage } from './utils';

export const user = (userObj: any) => {
  const tiktok = userObj.website?.startsWith('https://tiktok.com/') ? userObj.website : userObj.tiktok;

  const links: SocialLinkType[] = [];

  if (userObj.youtube?.url) {
    links.push({
      type: 'youtube',
      url: userObj.youtube.url,
      name: userObj.youtube.channel,
    });
  }
  if (userObj.instagram) {
    links.push({
      type: 'instagram',
      url: userObj.instagram,
      name: userObj.instagram.split('/').filter(Boolean).at(-1),
    });
  }
  if (tiktok) {
    links.push({
      type: 'tiktok',
      url: tiktok,
      name: tiktok.split('/').filter(Boolean).at(-1),
    });
  }

  return {
    id: userObj.id || userObj.userId,
    name: userObj.username || userObj.nombre,
    surname: userObj.usersurname || userObj.apellido,
    lastname: userObj.usersurname || userObj.apellido,
    nickname: userObj.nickname || 'Unknown',
    avatar: CDNImage(userObj.avatar_imagen || userObj.avatar_image || userObj.image || userObj.owner_image || userObj.imagen),
    email: userObj.email || '',
    level: gamificationMapper[userObj.level],
    verified: userObj.verified,
    follows: { total: userObj.follows },
    followers: { total: userObj.followers },
    isMyFollower: userObj.isMyFollower,
    isFollowed: userObj.following,
    biography: userObj.biography || '',
    language: userObj.lang,
    country: userObj.country,
    links,
  } as UserType;
};

export const collection = (collectionObj: any) => {
  let owner: UserType | undefined;
  if (collectionObj.owner) {
    try {
      owner = user(typeof collectionObj.owner === 'object' ? collectionObj.owner : JSON.parse(collectionObj.owner));
    } catch {
      console.error('could not parse owner');
    }
  }

  const recommendationCount = collectionObj.counter_content || collectionObj.counter;

  return {
    id: collectionObj.id,
    name: collectionObj.name,
    description: collectionObj.description,
    images: [collectionObj.image_1, collectionObj.image_2, collectionObj.image_3].map(CDNImage),
    ownerId: collectionObj.user_id_creator,
    followers: collectionObj.followers,
    recommendationCount: recommendationCount !== undefined ? parseInt(recommendationCount, 10) : undefined,
    isPrivate: collectionObj.private,
    owner,
    createdAt: new Date(collectionObj.created_at).toUTCString(),
    updatedAt: new Date(collectionObj.updated_at).toUTCString(),
  } as CollectionType;
};

export const comment = (commentObj: any) => {
  return {
    id: commentObj.recommendation_id,
    message: commentObj.mensaje,
    date: new Date(commentObj.fecha || commentObj.created_at),
    liked: commentObj.liked,
    likes: commentObj.counter_likes,
    user: {
      id: commentObj.truster_origen_id,
      name: commentObj.nombre,
      nickname: commentObj.nickname,
      avatar: CDNImage(commentObj.avatar_imagen),
      level: gamificationMapper[commentObj.level],
      verified: commentObj.verified,
    },
  } as CommentType;
};

export const product = (item: any): ProductType | undefined => {
  let preview: any = null;
  const url = item.preview_url || item.url || '';
  if (url.includes('youtube') && url.match(/v=([\w_-]+)/)) {
    preview = {
      type: 'youtube',
      url,
      id: url.match(/v=([\w_-]+)/).at(1),
    };
  }

  if (item.preview_url?.startsWith('https://p.scdn.co')) {
    preview = {
      type: 'music',
      url: item.preview_url,
      id: 'generic',
    };
  }

  const subcategory = (item.subcategory_id || item.profesional_subcategoria_id || item.subcategoryId || '').toString();
  const releaseDate = new Date(item.release_date || item.releaseDate);
  const category = findCategoryBySubCategory(subcategory);

  return {
    type: (category?.name || 'products') as CategoryEnum,
    id: item.productId || item.product_id || item.id,
    category,
    oldSubcategory: subcategory,
    subcategories: [],
    name: item.name,
    album: item.album_name,
    author: item.author,
    authors: item.author ? [item.author] : [],
    overview: item.overview,
    description: item.overview,
    image: CDNImage(item.image || item.imagen || item.images?.at(0)?.original),
    releaseDate: releaseDate.getFullYear() !== 1970 ? releaseDate : undefined,
    address: item.address,
    url: item.url,
    preview,
    genres: [],
    artists: [],
    media: [
      {
        type: 'image',
        source: CDNImage(item.image || item.imagen || item.images?.at(0)?.original),
      },
    ],
    runtime: 0,
    recommendations: {
      total: parseInt(item.totalRecommendations || item.recommendations || 0, 10),
      items: [] as RecommendationType[],
      edges: [],
    },
    ratings: [
      {
        source: 'chiles',
        value: item.ranking || 0,
        total: 5,
      },
    ],
    rating: item.ranking || 0,
    isSaved: item.isSaved || item.saved,
    popularity: item.popularity,
    likes: item.recommendations,
    totalRecomendations: parseInt(item.totalRecommendations || item.recommendations || 0, 10),
    providers: Object.keys(mapProviders)
      .filter((i) => {
        return !!item[i];
      })
      .map((i) => {
        return {
          ...mapProviders[item as string],
          url: item[i],
        };
      }),
    collections: null as any,
    geo: {
      lat: item.latitude,
      lng: item.longitude,
    },
    createAt: new Date(item.createdAt),
    updateAt: new Date(item.updateAt),
    extraInfo: {
      ratings: [],
    },
  } as ProductType;
};

export const gamification = ({ totalProgress, ...item }: any) => {
  return {
    current: totalProgress,
    progress: [
      'attribution',
      'collectionVisits',
      'collectionFollows',
      'followers',
      'sessions',
      'collections',
      'recommendations',
      'totalUserLikes',
    ].reduce((acc, current) => {
      if (!item[current]) {
        return acc;
      }
      return {
        ...acc,
        [current]: {
          value: item[current].value,
          max: item[current].maxValue,
        },
      };
    }, {}),
  };
};
